import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Users } from "lucide-react";
import { Button } from "../ui/button";
import { useAuth, useBooking } from "../../context";
import { toast } from "sonner";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { setShowBookingModal } = useBooking();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
    navigate("/");
  };

  return (
    <header 
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-strong py-3" : "py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2" data-testid="logo">
          <img 
            src="/logo.png" 
            alt="RainXpress Logo" 
            className="h-12 w-12 rounded-full object-cover"
          />
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          <button onClick={() => handleNavigation("/")} className={`transition ${location.pathname === "/" ? "text-cyan-400" : "text-slate-300 hover:text-cyan-400"}`}>Home</button>
          <button onClick={() => handleNavigation("/packages")} className={`transition ${location.pathname === "/packages" ? "text-cyan-400" : "text-slate-300 hover:text-cyan-400"}`}>Packages</button>
          <button onClick={() => handleNavigation("/trade")} className={`transition ${location.pathname === "/trade" ? "text-cyan-400" : "text-slate-300 hover:text-cyan-400"}`}>Trade</button>
          <button onClick={() => handleNavigation("/how-it-works")} className={`transition ${location.pathname === "/how-it-works" ? "text-cyan-400" : "text-slate-300 hover:text-cyan-400"}`}>How It Works</button>
          <button onClick={() => handleNavigation("/service-areas")} className={`transition ${location.pathname === "/service-areas" ? "text-cyan-400" : "text-slate-300 hover:text-cyan-400"}`}>Service Areas</button>
          <button onClick={() => handleNavigation("/contact")} className={`transition ${location.pathname === "/contact" ? "text-cyan-400" : "text-slate-300 hover:text-cyan-400"}`}>Contact</button>
          <button onClick={() => handleNavigation("/terms")} className={`transition ${location.pathname === "/terms" ? "text-cyan-400" : "text-slate-300 hover:text-cyan-400"}`}>T&Cs</button>
        </nav>

        <div className="flex items-center gap-3">
          <a 
            href="tel:0478054648" 
            className="hidden lg:flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition"
            data-testid="phone-link"
          >
            <Phone className="w-4 h-4" />
            <span className="text-sm font-medium">0478 054 648</span>
          </a>
          
          {/* Auth Buttons - Desktop */}
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <div className="relative group">
                <button 
                  onClick={() => handleNavigation("/account")}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 transition text-sm"
                  data-testid="account-btn"
                >
                  <Users className="w-4 h-4 text-cyan-400" />
                  <span className="text-slate-200">{user.name?.split(' ')[0]}</span>
                </button>
              </div>
            ) : (
              <button 
                onClick={() => handleNavigation("/account")}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 transition text-sm text-slate-200"
                data-testid="login-btn"
              >
                <Users className="w-4 h-4" />
                Login
              </button>
            )}
          </div>
          
          <Button 
            onClick={() => setShowBookingModal(true)}
            className="btn-primary-glow text-slate-900 font-semibold px-6 py-2 rounded-full"
            data-testid="header-book-now-btn"
          >
            Book Now
          </Button>
          <button 
            className="md:hidden text-white" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass-strong mt-2 mx-4 rounded-xl overflow-hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col p-4 gap-4">
              <button onClick={() => handleNavigation("/")} className="text-left py-2 text-slate-300 hover:text-cyan-400">Home</button>
              <button onClick={() => handleNavigation("/packages")} className="text-left py-2 text-slate-300 hover:text-cyan-400">Packages</button>
              <button onClick={() => handleNavigation("/trade")} className="text-left py-2 text-slate-300 hover:text-cyan-400">Trade & Business</button>
              <button onClick={() => handleNavigation("/how-it-works")} className="text-left py-2 text-slate-300 hover:text-cyan-400">How It Works</button>
              <button onClick={() => handleNavigation("/service-areas")} className="text-left py-2 text-slate-300 hover:text-cyan-400">Service Areas</button>
              <button onClick={() => handleNavigation("/contact")} className="text-left py-2 text-slate-300 hover:text-cyan-400">Contact</button>
              <button onClick={() => handleNavigation("/terms")} className="text-left py-2 text-slate-300 hover:text-cyan-400">T&Cs</button>
              
              {/* Auth - Mobile */}
              <div className="border-t border-slate-700 pt-4 mt-2">
                {user ? (
                  <>
                    <button 
                      onClick={() => handleNavigation("/account")} 
                      className="flex items-center gap-2 py-2 text-cyan-400"
                      data-testid="mobile-account-btn"
                    >
                      <Users className="w-4 h-4" />
                      My Account
                    </button>
                    <button 
                      onClick={handleLogout} 
                      className="flex items-center gap-2 py-2 text-slate-400 hover:text-red-400"
                      data-testid="mobile-logout-btn"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => handleNavigation("/account")} 
                    className="flex items-center gap-2 py-2 text-cyan-400"
                    data-testid="mobile-login-btn"
                  >
                    <Users className="w-4 h-4" />
                    Login / Sign Up
                  </button>
                )}
              </div>
              
              <a href="tel:0478054648" className="flex items-center gap-2 py-2 text-cyan-400">
                <Phone className="w-4 h-4" />
                0478 054 648
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
